import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const MYSHOP_TABS = Object.freeze({
  products: { defaultView: "admin", views: ["admin", "customer", "orders"] },
  "sell-center": { defaultView: null, views: [] },
  deal: { defaultView: "in", views: ["in", "out"] },
  pub: { defaultView: "global", views: ["global", "shop"] },
});

export const SHOP_PUB_SECTIONS = Object.freeze([
  "pub-follower",
  "pub-category",
  "btn-image-admin",
  "pub-follow-images",
  "slider-shop",
]);

export const DEFAULT_SHOP_PUB_SECTION = "slider-shop";

const legacyLocation = (params) => {
  if (params.get("sel")) return { tab: "sell-center", view: null, section: null };
  if (params.get("td")) return { tab: "deal", view: "in", section: null };
  if (params.get("fd")) return { tab: "deal", view: "out", section: null };
  if (params.get("mb")) {
    return {
      tab: "pub",
      view: params.get("BPUB") === "false" ? "shop" : "global",
      section: params.get("BPUB") === "false" ? DEFAULT_SHOP_PUB_SECTION : null,
    };
  }
  if (params.get("odr")) return { tab: "products", view: "orders", section: null };
  return null;
};

export const resolveMyShopLocation = (params) => {
  const legacy = !params.get("tab") ? legacyLocation(params) : null;
  const requestedTab = legacy?.tab || params.get("tab");
  const tab = Object.prototype.hasOwnProperty.call(MYSHOP_TABS, requestedTab) ? requestedTab : "products";
  const config = MYSHOP_TABS[tab];
  const requestedView = legacy?.view ?? params.get("view");
  const view = config.views.includes(requestedView) ? requestedView : config.defaultView;
  const requestedSection = legacy?.section ?? params.get("section");
  const section = tab === "pub" && view === "shop"
    ? (SHOP_PUB_SECTIONS.includes(requestedSection) ? requestedSection : DEFAULT_SHOP_PUB_SECTION)
    : null;
  return { tab, view, section };
};

export const updateMyShopParams = (currentParams, updates) => {
  const next = new URLSearchParams(currentParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") next.delete(key);
    else next.set(key, value);
  });
  return next;
};

const LEGACY_NAVIGATION_PARAMS = ["ppb", "sel", "td", "fd", "mb", "odr", "BPUB"];
const SELL_WIZARD_PARAMS = ["ip", "ic"];

export const normalizedMyShopParams = (currentParams, location) => {
  const next = updateMyShopParams(currentParams, location);
  if (!location.view) next.delete("view");
  if (!location.section) next.delete("section");
  LEGACY_NAVIGATION_PARAMS.forEach((key) => next.delete(key));
  // Sell Your Product wizard params only belong to Sell Center; carrying
  // them into another tab would reopen a stale draft on the way back.
  if (location.tab !== "sell-center") SELL_WIZARD_PARAMS.forEach((key) => next.delete(key));
  return next;
};

export const useMyShopNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = resolveMyShopLocation(searchParams);
  const normalizedSearch = normalizedMyShopParams(searchParams, location).toString();

  useEffect(() => {
    if (normalizedSearch !== searchParams.toString()) {
      setSearchParams(new URLSearchParams(normalizedSearch), { replace: true });
    }
  }, [normalizedSearch, searchParams, setSearchParams]);

  const setMyShopQuery = useCallback((nextLocation) => {
    const updatedParams = updateMyShopParams(searchParams, nextLocation);
    const resolved = resolveMyShopLocation(updatedParams);
    setSearchParams(normalizedMyShopParams(updatedParams, resolved));
  }, [searchParams, setSearchParams]);

  const changeTab = useCallback((tab) => {
    const config = MYSHOP_TABS[tab] || MYSHOP_TABS.products;
    const next = { tab: MYSHOP_TABS[tab] ? tab : "products", view: config.defaultView, section: null };
    if (tab === "pub" && config.defaultView === "shop") next.section = DEFAULT_SHOP_PUB_SECTION;
    setSearchParams(normalizedMyShopParams(searchParams, next));
  }, [searchParams, setSearchParams]);

  const changeView = useCallback((view) => {
    const config = MYSHOP_TABS[location.tab];
    const validView = config.views.includes(view) ? view : config.defaultView;
    const section = location.tab === "pub" && validView === "shop" ? DEFAULT_SHOP_PUB_SECTION : null;
    setSearchParams(normalizedMyShopParams(searchParams, { tab: location.tab, view: validView, section }));
  }, [location.tab, searchParams, setSearchParams]);

  const changeSection = useCallback((section) => {
    const validSection = SHOP_PUB_SECTIONS.includes(section) ? section : DEFAULT_SHOP_PUB_SECTION;
    setSearchParams(normalizedMyShopParams(searchParams, { tab: "pub", view: "shop", section: validSection }));
  }, [searchParams, setSearchParams]);

  return { ...location, setMyShopQuery, changeTab, changeView, changeSection };
};

// Sell Your Product keeps its wizard progress in the URL: `ic` is the chosen
// final category (Info step), `ip` the product already created (Filters /
// Images steps). Either one means the Sell flow is the open Sell Center page.
export const hasSellDraftParams = (params) => Boolean(params.get("ip") || params.get("ic"));

// Which Sell Center page is open (hub = null, "sellProduct", "dropshippingIn"…).
// The URL only forces "sellProduct" when a wizard draft is in it; it never
// resets the choice to the hub -- choosing a category adds `ic` without `ip`,
// and resetting on that change unmounted the wizard right after Categories.
// `sellSessionKey` changes when the draft params disappear ("add another
// product", browser Back to the start) so the wizard can remount empty.
export const useSellCenterSelection = (activeTab) => {
  const [searchParams] = useSearchParams();
  const hasDraft = hasSellDraftParams(searchParams);
  const [selectedSellType, setSelectedSellType] = useState(() => (hasDraft ? "sellProduct" : null));
  const [sellSessionKey, setSellSessionKey] = useState(0);
  const hadDraftRef = useRef(hasDraft);

  useEffect(() => {
    if (activeTab !== "sell-center") return;
    if (hasDraft) setSelectedSellType("sellProduct");
    else if (hadDraftRef.current) setSellSessionKey((key) => key + 1);
    hadDraftRef.current = hasDraft;
  }, [activeTab, hasDraft]);

  return { selectedSellType, setSelectedSellType, sellSessionKey };
};
